import React, { useState, useEffect, useContext } from 'react';
import ApiClient, { getImageUrl } from '../../Services/ApiClient';
import { validateText } from '../../Utils/validation';
import AuthContext from '../../store/authContext';
import { useAlert } from '../ui/alertProvider';
import { useResources, useComponentResources } from '../../store/resourcesProvider';
import { Item } from '../../../Models/Item';
import ConfirmModal from '../ui/confirmModal';

// use ApiBaseUrl from ApiClient for server static files

const ItemsAdmin: React.FC = () => {
    const { isAdmin } = useContext(AuthContext);
    const [items, setItems] = useState<Item[]>([]);
    const [editing, setEditing] = useState<Partial<Item> | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const alert = useAlert();                                                       
    const resources = useResources();
    // preload and read component-scoped resources for admin items page (includes userId automatically)
    const pageRes = useComponentResources('ItemsAdmin', [
        'ItemsAdmin.Title','ItemsAdmin.AddNew','ItemsAdmin.Prev','ItemsAdmin.Next',
        'ItemsAdmin.Col.Id','ItemsAdmin.Col.Title','ItemsAdmin.Col.Ranking','ItemsAdmin.Col.Type','ItemsAdmin.Col.Image','ItemsAdmin.Col.Research','ItemsAdmin.Col.Symptoms','ItemsAdmin.Col.Treatments','ItemsAdmin.Col.Actions',
        'ItemsAdmin.Type.General','ItemsAdmin.Type.Vocal',
        'ItemsAdmin.Edit','ItemsAdmin.Delete','ItemsAdmin.ConfirmDelete',
        'ItemsAdmin.Saving','ItemsAdmin.Save','ItemsAdmin.Cancel',
        'ItemsAdmin.EditTitle','ItemsAdmin.NewTitle',
        'ItemsAdmin.Label.Title','ItemsAdmin.Label.Ranking','ItemsAdmin.Label.Type','ItemsAdmin.Label.Description','ItemsAdmin.Label.Research','ItemsAdmin.Label.Symptoms','ItemsAdmin.Label.Treatments','ItemsAdmin.Label.Image'
    ]);

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [showModal, setShowModal] = useState<boolean>(false);

    useEffect(() => {
        if (!isAdmin) return;
        loadItems();
    }, [isAdmin, page, pageSize]);

    const loadItemsForUser = async (userId: number) => {
        try {
            const data: any = await ApiClient.get(`/item/user/${userId}`);
            setItems(data || []);
        } catch (err) {
            console.error('Failed to load user items', err);
            alert.showAlert('Failed to load user items: ' + ((err as any)?.message || String(err)), 'error');
        }
    };

    const loadItems = async () => {
        try {
            const data: any = await ApiClient.get(`/item/paged?page=${page}&pageSize=${pageSize}`);
            setItems(data?.items || data?.Items || []);
            setTotal(data?.totalCount ?? data?.TotalCount ?? 0);
        } catch (err) {
            console.error('Failed to load items', err);
            alert.showAlert('Failed to load items: ' + ((err as any)?.message || String(err)), 'error');
        }
    };

    const startNew = () => {
        setEditing({ title: '', ranking: 0, type: 0, text: '', imageId: undefined, researchSummary: '', symptoms: [], treatmentMethods: [] });
        setFile(null);
        setPreviewUrl(null);
        setShowModal(true);
    };

    const edit = (it: Item) => {
        setEditing({ ...it });
        const fileName = it.imageFileName || it.imageName || undefined;
        setPreviewUrl(fileName ? getImageUrl(fileName) || null : null);
        setShowModal(true);
    };

    const cancel = () => {
        setEditing(null);
        setFile(null);
        setPreviewUrl(null);
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setFile(f);
        if (f) setPreviewUrl(URL.createObjectURL(f));
    };

    const uploadFile = async (): Promise<{ imageId?: number; fileName?: string } | null> => {
        if (!file) return null;
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res: any = await ApiClient.post('/item/upload', fd);
            return res;
        } catch (err) {
            console.error('Upload failed', err);
            alert.showAlert('Upload failed: ' + ((err as any)?.message || String(err)), 'error');
            return null;
        }
    };

    const save = async () => {
        if (!editing) return;
        setLoading(true);
        try {
            // client-side validation
            const errs: any = {};
            const titleErr = validateText(editing.title || '', 1, 255);
            if (titleErr) errs.title = titleErr;
            const researchErr = editing.researchSummary ? validateText(editing.researchSummary, 0, 2000) : null;
            if (researchErr) errs.research = researchErr;
            if (Object.keys(errs).length > 0) {
                const msg = Object.values(errs).join('; ');
                alert.showAlert(msg, 'error');
                setLoading(false);
                return;
            }
            if (file) {
                const up = await uploadFile();
                if (up && up.imageId) editing.imageId = up.imageId;
                if (up && up.fileName) editing.imageFileName = up.fileName;
            }

            if (editing.id && editing.id > 0) {
                await ApiClient.put('/item', editing);
            } else {
                await ApiClient.post('/item', editing);
            }
            await loadItems();
            cancel();
            setShowModal(false);
            alert.showAlert('Item saved successfully.', 'success');
        } catch (err) {
            console.error('Save failed', err);
            alert.showAlert('Save failed: ' + ((err as any)?.message || String(err)), 'error');
        } finally {
            setLoading(false);
        }
    };

    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

    const remove = async (id?: number) => {
        if (!id) return;
        setConfirmDeleteId(id);
    };

    const performDelete = async (id: number | null) => {
        if (!id) return;
        setConfirmLoading(true);
        try {
            await ApiClient.delete(`/item/${id}`);
            await loadItems();
            alert.showAlert('Item deleted.', 'success');
        } catch (err) {
            console.error('Delete failed', err);
            alert.showAlert('Delete failed: ' + ((err as any)?.message || String(err)), 'error');
        } finally {
            setConfirmLoading(false);
            setConfirmDeleteId(null);
        }
    };

    if (!isAdmin) return <div className="items-admin">{resources.get('Resources.NoPermission','אין הרשאה')}</div>;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="items-admin">
            <h2 className="items-title">{pageRes['ItemsAdmin.Title'] ?? resources.get('ItemsAdmin.Title','ניהול פריטים')}</h2>

            <div className="table-controls">
                <button className="btn btn-primary" onClick={startNew}>{pageRes['ItemsAdmin.AddNew'] ?? resources.get('ItemsAdmin.AddNew','הוסף פריט חדש')}</button>
                <div style={{marginLeft: 'auto', display: 'flex', alignItems:'center', gap:8}}>
                    <button className="btn" onClick={() => setPage(Math.max(1, page - 1))}>{pageRes['ItemsAdmin.Prev'] ?? resources.get('ItemsAdmin.Prev','הקודם')}</button>
                    <span>{page} / {totalPages}</span>
                    <button className="btn" onClick={() => setPage(Math.min(totalPages, page + 1))}>{pageRes['ItemsAdmin.Next'] ?? resources.get('ItemsAdmin.Next','הבא')}</button>
                    <select value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                </div>

            {/* Mobile-friendly card list already present - ensure actions minimal like patients page */}
            </div>

            <div className="table-container">
                <table className="items-table">
                    <thead>
                        <tr>
                            <th>{pageRes['ItemsAdmin.Col.Id'] ?? resources.get('ItemsAdmin.Col.Id','מזהה')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Title'] ?? resources.get('ItemsAdmin.Col.Title','כותרת')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Ranking'] ?? resources.get('ItemsAdmin.Col.Ranking','דירוג')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Type'] ?? resources.get('ItemsAdmin.Col.Type','סוג')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Image'] ?? resources.get('ItemsAdmin.Col.Image','תמונה')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Research'] ?? resources.get('ItemsAdmin.Col.Research','מחקר')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Symptoms'] ?? resources.get('ItemsAdmin.Col.Symptoms','תסמינים')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Treatments'] ?? resources.get('ItemsAdmin.Col.Treatments','טיפולים')}</th>
                            <th>{pageRes['ItemsAdmin.Col.Actions'] ?? resources.get('ItemsAdmin.Col.Actions','פעולות')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(it => {
                          const imgUrl = getImageUrl(it.imageFileName);
                          return (
                            <tr key={it.id}>
                                <td>{it.id}</td>
                                <td>{it.title}</td>
                                <td>{it.ranking}</td>
                                <td>{it.type === 0 ? (pageRes['ItemsAdmin.Type.General'] ?? resources.get('ItemsAdmin.Type.General','כללי')) : (pageRes['ItemsAdmin.Type.Vocal'] ?? resources.get('ItemsAdmin.Type.Vocal','קולי'))}</td>
                                <td>
                                  {imgUrl ? (
                                    <img className="preview-img" src={imgUrl} alt="img" loading="lazy" onError={(e)=>{ const t=e.currentTarget as HTMLImageElement; t.onerror=null; t.classList.add('img-missing'); }} />
                                  ) : (
                                    <div className="img-missing" style={{padding:8}}>{pageRes['Action.MissingImage'] ?? resources.get('Action.MissingImage','תמונה אינה זמינה')}</div>
                                  )}
                                </td>
                                <td style={{maxWidth:240}}>{(it.researchSummary || '').substring(0,200)}</td>
                                <td style={{maxWidth:200}}>{(it.symptoms || []).join(', ')}</td>
                                <td style={{maxWidth:200}}>{(it.treatmentMethods || []).join(', ')}</td>
                                <td>
                                    <button className="btn" onClick={() => edit(it)}>{pageRes['ItemsAdmin.Edit'] ?? resources.get('ItemsAdmin.Edit','ערוך')}</button>
                                    <button className="btn btn-danger" onClick={() => setConfirmDeleteId(it.id)}>{pageRes['ItemsAdmin.Delete'] ?? resources.get('ItemsAdmin.Delete','מחק')}</button>
                                </td>
                            </tr>
                          );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile-friendly card list (visible when table is hidden by CSS) */}
            <div className="cards-list">
                {items.map(it => {
                    const imgUrl = getImageUrl(it.imageFileName);
                    return (
                        <div key={it.id} className="item-card">
                            <div style={{display:'flex', gap:12, alignItems:'center'}}>
                                {imgUrl ? <img src={imgUrl} alt={it.title} style={{width:80, height:80, objectFit:'cover', borderRadius:6}} /> : <div style={{width:80, height:80, background:'#f2f2f2', borderRadius:6}} />}
                                <div style={{flex:1}}>
                                    <div style={{fontWeight:700}}>{it.title}</div>
                                    <div style={{color:'#666', fontSize:12}}>{resources.get(it.type === 0 ? 'ItemsAdmin.Type.General' : 'ItemsAdmin.Type.Vocal','')}</div>
                                </div>
                                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                    <button className="btn" onClick={() => edit(it)}>{resources.get('ItemsAdmin.Edit','ערוך')}</button>
                                    <button className="btn btn-danger" onClick={() => setConfirmDeleteId(it.id)}>{resources.get('ItemsAdmin.Delete','מחק')}</button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ConfirmModal
                open={confirmDeleteId != null}
                title={pageRes['ItemsAdmin.Delete'] ?? resources.get('ItemsAdmin.Delete','מחק')}
                message={pageRes['ItemsAdmin.ConfirmDelete'] ?? resources.get('ItemsAdmin.ConfirmDelete','האם אתה בטוח?')}
                loading={confirmLoading}
                onConfirm={() => performDelete(confirmDeleteId)}
                onCancel={() => setConfirmDeleteId(null)}
            />

            {showModal && editing && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close" onClick={() => { setShowModal(false); cancel(); }}>&times;</button>
                        <h3 className="modal-title">{editing.id ? (pageRes['ItemsAdmin.EditTitle'] ?? resources.get('ItemsAdmin.EditTitle','ערוך פריט')) : (pageRes['ItemsAdmin.NewTitle'] ?? resources.get('ItemsAdmin.NewTitle','פריט חדש'))}</h3>
                        <form onSubmit={(e) => { e.preventDefault(); save(); }}>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Title'] ?? resources.get('ItemsAdmin.Label.Title','כותרת')}</label>
                                <textarea className="form-input" style={{minHeight:40}} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Ranking'] ?? resources.get('ItemsAdmin.Label.Ranking','דירוג')}</label>
                                <input className="form-input" type="number" value={editing.ranking} onChange={e => setEditing({ ...editing, ranking: parseInt(e.target.value || '0') })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Type'] ?? resources.get('ItemsAdmin.Label.Type','סוג')}</label>
                                <select className="form-select" value={editing.type} onChange={e => setEditing({ ...editing, type: parseInt(e.target.value) })}>
                                    <option value={0}>{pageRes['ItemsAdmin.Type.General'] ?? resources.get('ItemsAdmin.Type.General','כללי')}</option>
                                    <option value={1}>{pageRes['ItemsAdmin.Type.Vocal'] ?? resources.get('ItemsAdmin.Type.Vocal','קולי')}</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Description'] ?? resources.get('ItemsAdmin.Label.Description','תיאור')}</label>
                                <textarea className="form-textarea" style={{minHeight:100}} value={editing.text} onChange={e => setEditing({ ...editing, text: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Research'] ?? resources.get('ItemsAdmin.Label.Research','סיכום מחקר')}</label>
                                <textarea className="form-textarea" style={{minHeight:80}} value={editing.researchSummary || ''} onChange={e => setEditing({ ...editing, researchSummary: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Symptoms'] ?? resources.get('ItemsAdmin.Label.Symptoms','תסמינים (מופרדים בפסיק)')}</label>
                                <textarea className="form-input" style={{minHeight:60}} value={(editing.symptoms || []).join(', ')} onChange={e => setEditing({ ...editing, symptoms: e.target.value.split(',').map(s => s.trim()).filter(s=>s.length>0) })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Treatments'] ?? resources.get('ItemsAdmin.Label.Treatments','שיטות טיפול (מופרדות בפסיק)')}</label>
                                <textarea className="form-input" style={{minHeight:60}} value={(editing.treatmentMethods || []).join(', ')} onChange={e => setEditing({ ...editing, treatmentMethods: e.target.value.split(',').map(s => s.trim()).filter(s=>s.length>0) })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['ItemsAdmin.Label.Image'] ?? resources.get('ItemsAdmin.Label.Image','תמונה')}</label>
                                <input className="form-input" type="file" accept="image/*" onChange={onFileChange} />
                                {previewUrl ? <div className="preview-wrap"><img className="preview-img" src={previewUrl} loading="lazy" onError={(e)=>{ const t=e.currentTarget as HTMLImageElement; t.onerror=null; t.classList.add('img-missing'); }} /></div> : null}
                            </div>
                            <div className="form-actions">
                                <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? (pageRes['ItemsAdmin.Saving'] ?? resources.get('ItemsAdmin.Saving','שומר...')) : (pageRes['ItemsAdmin.Save'] ?? resources.get('ItemsAdmin.Save','שמור'))}</button>
                                <button className="btn" type="button" onClick={() => { setShowModal(false); cancel(); }}>{pageRes['ItemsAdmin.Cancel'] ?? resources.get('ItemsAdmin.Cancel','בטל')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItemsAdmin;
